import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { PriceLockTimer } from './PriceLockTimer';
import { OrderSummary } from './OrderSummary';
import { IncludedList, GuaranteeCard, TestimonialsBlock } from './CheckoutExtras';

export function CheckoutShell({ cfg, children, banner }) {
  return (
    <div className="min-h-screen bg-[#f6f3ee] text-slate-900" data-testid="checkout-page" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 sm:pt-7 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="checkout-logo">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9" />
            <span className="font-extrabold tracking-tight text-lg hidden sm:inline">CELPIPMax</span>
          </Link>
          <Link to="/dashboard" className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors" data-testid="checkout-back-link">
            <ArrowLeft size={13} />{cfg?.label_back_link || 'Back to dashboard'}
          </Link>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="secure-checkout-pill">
          <Lock size={12} className="text-emerald-600" />{cfg?.label_secure_pill || 'Secure checkout'}
        </span>
      </header>
      {banner}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8 pb-40 lg:pb-16">{children}</main>
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 text-center">
        {cfg?.footer_note && <p className="text-sm text-slate-500" data-testid="checkout-footer-note">{cfg.footer_note}</p>}
        <p className="text-xs text-slate-400 mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1">
          <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          <Link to="/refund-policy" className="hover:text-slate-700">Refund Policy</Link>
          <Link to="/contact" className="hover:text-slate-700">Contact</Link>
        </p>
      </footer>
    </div>
  );
}

/**
 * Two-column layout. Desktop: order + proof on the left, sticky payment on the
 * right. Mobile: order → payment → guarantee/included/proof, so the card form
 * is never buried under marketing copy.
 */
export function CheckoutColumns({ cfg, quote, summary, payment }) {
  return (
    <div className="flex flex-col gap-4 sm:gap-6 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:items-start">
      <div className="order-1 space-y-3 sm:space-y-4 lg:col-start-1 lg:row-start-1">
        <PriceLockTimer cfg={cfg} />
        <OrderSummary cfg={cfg} quote={quote} {...summary} />
      </div>
      <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-6">{payment}</div>
      <div className="order-3 space-y-5 sm:space-y-8 lg:col-start-1 lg:row-start-2 pt-1 sm:pt-2">
        <GuaranteeCard cfg={cfg} />
        <IncludedList cfg={cfg} />
        <TestimonialsBlock cfg={cfg} />
      </div>
    </div>
  );
}
